// Export to accounting. Finance → Export to accounting (Finance Full and "Can see costs and prices"): the supplier bills,
// payments and credit notes in a date range, ticked and downloaded as the accounting system's own import file(s); each
// is then marked Exported. Settings → Finance → Accounting export (Admins): the system, date format and account codes.
import { ApiError, confirmBox, esc, formatDate, get, money, orgToday, put, toast } from "../lib.js";
import { onAction, onForm, route } from "../state.js";
import { canExportToAccounting, errorPanel, loadingPanel, renderShell, setContent } from "../shell.js";
import { AX_DATE_FORMATS, AX_ITEMS, AX_NO_PAYMENT_IMPORT, AX_SYSTEMS, AX_TYPE_LABEL, axDefaultDateFormat, axSystemShort, type AxSettings } from "../../../shared/accexport.js";

// ---- Settings → Finance → Accounting export -------------------------------------------------------------------------------
export async function loadAxSettings(): Promise<any> { return get("/api/org/accounting-export/settings"); }
export function axSettingsHtml(d: { settings: AxSettings; currency: string; problems?: Record<string, string> }, bad: Record<string, string> = {}): string {
  const s = d.settings;
  const opt = (v: string, l: string, cur: string) => `<option value="${esc(v)}" ${v === cur ? "selected" : ""}>${esc(l)}</option>`;
  return `<form data-form="ax-settings" class="ax-settings" novalidate>
<p class="muted ax-intro">Used by <a href="/app/finance/export" data-link>Finance → Export to accounting</a>. Every line in the file takes the account code (and tax code) of its Falcon item.</p>
<div class="form-message" role="alert" hidden></div>
<div class="ax-top">
<div class="form-group"><label for="ax-system">Your accounting system</label><select id="ax-system" name="system">${AX_SYSTEMS.map(([k, l]) => opt(k, l, s.system)).join("")}</select></div>
<div class="form-group"><label for="ax-date">Date format for the file</label><select id="ax-date" name="dateFormat">${AX_DATE_FORMATS.map((f) => opt(f, f, s.dateFormat)).join("")}</select></div>
<div class="form-group"><label for="ax-cur">Currency</label><input id="ax-cur" value="${esc(d.currency)}" readonly aria-readonly="true"><small class="muted">From Settings → Finance → Organisation profile (trading currency).</small></div>
</div>
<div class="ax-codes" role="table" aria-label="Account codes">
<div class="ax-code-head" role="row"><span role="columnheader">Falcon item</span><span role="columnheader">Account code</span><span role="columnheader">Tax code</span></div>
${AX_ITEMS.map(([k, l]) => `<div class="ax-code-row ${bad[k] ? "is-bad" : ""}" role="row" data-item="${k}">
<span class="ax-code-item" role="cell">${esc(l)}</span>
<span role="cell" class="ax-code-cell"><input name="acc_${k}" value="${esc(s.codes[k].account)}" maxlength="60" aria-label="${esc(l)}: account code" ${bad[k] ? 'aria-invalid="true"' : ""}>${bad[k] ? `<small class="ax-err">${esc(bad[k])}</small>` : ""}</span>
<span role="cell" class="ax-code-cell">${k === "bank" ? '<span class="muted">—</span>' : `<input name="tax_${k}" value="${esc(s.codes[k].tax)}" maxlength="60" placeholder="Optional" aria-label="${esc(l)}: tax code">`}</span>
</div>`).join("")}
</div>
<div class="form-actions"><button type="submit" class="primary">Save accounting export</button></div>
</form>`;
}
let lastSystem = "";
document.addEventListener("focusin", (e) => { const t = e.target as HTMLSelectElement; if (t.id === "ax-system") lastSystem = t.value; });
document.addEventListener("change", (e) => {
  const t = e.target as HTMLSelectElement;
  if (t.id !== "ax-system") return;
  // The date format follows the system unless it was changed by hand.
  const df = document.getElementById("ax-date") as HTMLSelectElement | null;
  if (df && (!lastSystem || df.value === axDefaultDateFormat(lastSystem))) df.value = axDefaultDateFormat(t.value);
  lastSystem = t.value;
});
onForm("ax-settings", async (form) => {
  const v = (n: string) => (form.querySelector<HTMLInputElement | HTMLSelectElement>(`[name="${n}"]`)?.value ?? "").trim();
  const codes: Record<string, { account: string; tax: string }> = {};
  const bad: Record<string, string> = {};
  for (const [k] of AX_ITEMS) { codes[k] = { account: v(`acc_${k}`), tax: v(`tax_${k}`) }; if (!codes[k].account) bad[k] = "Enter the account code."; }
  const settings = { system: v("system"), dateFormat: v("dateFormat"), codes } as unknown as AxSettings;
  const show = (b: Record<string, string>, msg: string) => {
    const cur = (document.getElementById("ax-cur") as HTMLInputElement | null)?.value || "";
    form.outerHTML = axSettingsHtml({ settings, currency: cur }, b);
    const f = document.querySelector<HTMLElement>('form[data-form="ax-settings"] .form-message');
    if (f) { f.hidden = false; f.textContent = msg; }
    document.querySelector<HTMLInputElement>(".ax-code-row.is-bad input")?.focus();
  };
  if (Object.keys(bad).length) return show(bad, "Enter an account code in each box marked in red.");
  try {
    await put("/api/org/accounting-export/settings", settings);
    toast("Accounting export saved.");
    show({}, "");
    const m = document.querySelector<HTMLElement>('form[data-form="ax-settings"] .form-message'); if (m) m.hidden = true;
  } catch (err) {
    if (err instanceof ApiError && err.details?.fields) return show(err.details.fields, err.message);
    toast((err as Error).message, "error");
  }
});

// ---- Finance → Export to accounting ---------------------------------------------------------------------------------------
interface Rec { type: "bill" | "payment" | "credit" | "refund"; id: string; date: string; number: string; supplier: string; po: string; currency: string; amount: number; trading: number | null; exportedAt: string | null; exportedBy: string; description: string }
let X: { from: string; to: string; include: "new" | "all"; types: Set<string>; data: any; ticked: Set<string> } | null = null;
const key = (r: Rec) => `${r.type}:${r.id}`;
function lastMonth(): [string, string] {
  const [y, m] = orgToday().split("-").map(Number);
  const py = m === 1 ? y - 1 : y, pm = m === 1 ? 12 : m - 1;
  const last = new Date(Date.UTC(py, pm, 0)).getUTCDate();
  const mm = String(pm).padStart(2, "0");
  return [`${py}-${mm}-01`, `${py}-${mm}-${String(last).padStart(2, "0")}`];
}

route("/app/finance/export", async () => {
  renderShell({ active: "financeexport", title: "Export to accounting", subtitle: "Bills, payments and credit notes for your accounting system.", content: loadingPanel() });
  if (!canExportToAccounting()) return setContent(errorPanel("Export to accounting needs Full access to Finance and \"Can see costs and prices\". Ask an Admin."));
  if (!X) { const [from, to] = lastMonth(); X = { from, to, include: "new", types: new Set(["bill", "payment", "credit"]), data: null, ticked: new Set() }; }
  await reload();
});
async function reload(): Promise<void> {
  const x = X!;
  try {
    x.data = await get(`/api/org/accounting-export/records?from=${x.from}&to=${x.to}&include=${x.include}&types=${[...x.types].join(",")}`);
  } catch (err) { return setContent(errorPanel((err as Error).message)); }
  const shown = new Set((x.data.records as Rec[]).map(key));
  x.ticked = new Set([...x.ticked].filter((k) => shown.has(k)));
  draw();
}
function draw(): void {
  const x = X!, d = x.data;
  if (!d.complete) {
    return setContent(`<div class="panel ax-setup"><h3>Set up the accounting export first</h3><p>Choose your accounting system and enter an account code for each Falcon item.</p>
${d.isAdmin ? '<p><a class="primary" href="/app/settings/finance?tab=accexport" data-link>Open Settings → Finance → Accounting export</a></p>' : "<p class=\"muted\">Ask an Admin to fill in Settings → Finance → Accounting export.</p>"}</div>`);
  }
  const recs = d.records as Rec[];
  const tick = (k: string, label: string) => `<label class="check-row"><input type="checkbox" data-ax-type="${k}" ${x.types.has(k) ? "checked" : ""}><span>${esc(label)}</span></label>`;
  setContent(`<div class="ax" id="ax">
<div class="panel ax-filters">
<div class="ax-filter-row">
<div class="form-group"><label for="ax-from">From date</label><input type="date" id="ax-from" name="axFrom" value="${esc(x.from)}"></div>
<div class="form-group"><label for="ax-to">To date</label><input type="date" id="ax-to" name="axTo" value="${esc(x.to)}"></div>
<div class="form-group"><label for="ax-include">Include</label><select id="ax-include"><option value="new" ${x.include === "new" ? "selected" : ""}>Not yet exported</option><option value="all" ${x.include === "all" ? "selected" : ""}>All in date range</option></select></div>
</div>
<div class="ax-types" role="group" aria-label="Record types">${tick("bill", "Supplier bills")}${tick("payment", "Payments and refunds")}${tick("credit", "Claims / credit notes")}</div>
</div>
<div class="panel ax-list">
<div class="table-wrap"><table class="ax-table"><thead><tr><th class="ax-c"><input type="checkbox" id="ax-all" aria-label="Tick all" ${recs.length && recs.every((r) => x.ticked.has(key(r))) ? "checked" : ""}></th><th>Date</th><th>Type</th><th>Supplier</th><th>PO</th><th class="num">Amount</th><th>Status</th></tr></thead>
<tbody>${recs.map((r) => `<tr data-k="${esc(key(r))}"><td class="ax-c"><input type="checkbox" data-ax-tick="${esc(key(r))}" ${x.ticked.has(key(r)) ? "checked" : ""} aria-label="Tick ${esc(AX_TYPE_LABEL[r.type])} ${esc(r.number)}"></td>
<td>${esc(formatDate(r.date))}</td><td>${esc(AX_TYPE_LABEL[r.type])}<br><small class="muted">${esc(r.number)}</small></td><td>${esc(r.supplier)}</td><td>${esc(r.po)}</td><td class="num">${r.type === "credit" || r.type === "refund" ? "−" : ""}${esc(money(r.amount, r.currency))}</td>
<td>${r.exportedAt ? `<span class="ax-done">Exported ${esc(formatDate(r.exportedAt.slice(0, 10)))}</span>${r.exportedBy ? `<br><small class="muted">${esc(r.exportedBy)}</small>` : ""}` : '<span class="muted">Not exported</span>'}</td></tr>`).join("")
  || `<tr><td colspan="7"><div class="empty">${x.include === "new" ? "Nothing waiting to export in these dates." : "Nothing in these dates."}</div></td></tr>`}</tbody></table></div>
<div class="ax-foot"><span id="ax-sum" role="status">${sumText()}</span>
<button type="button" class="primary" data-action="ax-download">Download ${esc(axSystemShort(d.system))} file</button></div>
<p class="ax-msg sw-err" id="ax-msg" role="alert" hidden></p>
${AX_NO_PAYMENT_IMPORT.includes(d.system) && x.types.has("payment") ? `<small class="muted">${esc(d.systemLabel)} can't import payments from a file: the payments file is included so you can match them in its bank reconciliation.</small>` : ""}
</div></div>`);
}
/** "<n> records selected · net <amount>": bills less credit notes and payments, in the trading currency. */
function sumText(): string {
  const x = X!, recs = (x.data.records as Rec[]).filter((r) => x.ticked.has(key(r)));
  const parts = recs.map((r) => (r.trading === null ? null : r.type === "bill" || r.type === "refund" ? r.trading : -r.trading));
  const net = parts.some((p) => p === null) ? null : parts.reduce((t: number, p) => t + (p as number), 0);
  return `${recs.length} record${recs.length === 1 ? "" : "s"} selected · net ${net === null ? "— (exchange rate missing)" : money(net, x.data.currency)}`;
}
function refreshSum(): void {
  const s = document.getElementById("ax-sum"); if (s) s.textContent = sumText();
  const recs = X!.data.records as Rec[];
  const all = document.getElementById("ax-all") as HTMLInputElement | null;
  if (all) all.checked = !!recs.length && recs.every((r) => X!.ticked.has(key(r)));
  const m = document.getElementById("ax-msg"); if (m && X!.ticked.size) m.hidden = true;
}
document.addEventListener("change", (e) => {
  const t = e.target as HTMLInputElement;
  if (!X || !t.closest?.("#ax")) return;
  // Dates: the ISO value is on the named input (the date box shows dd/mm/yyyy).
  if (t.name === "axFrom" || t.name === "axTo") {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(t.value)) return;
    if (t.name === "axFrom") X.from = t.value; else X.to = t.value;
    if (X.from > X.to) { const m = document.getElementById("ax-msg"); if (m) { m.hidden = false; m.textContent = "The From date must be on or before the To date."; } return; }
    void reload();
  } else if (t.id === "ax-include") { X.include = t.value as "new" | "all"; void reload(); }
  else if (t.dataset.axType) { if (t.checked) X.types.add(t.dataset.axType); else X.types.delete(t.dataset.axType); void reload(); }
  else if (t.id === "ax-all") { for (const r of X.data.records as Rec[]) { if (t.checked) X.ticked.add(key(r)); else X.ticked.delete(key(r)); } document.querySelectorAll<HTMLInputElement>("[data-ax-tick]").forEach((c) => (c.checked = t.checked)); refreshSum(); }
  else if (t.dataset.axTick) { if (t.checked) X.ticked.add(t.dataset.axTick); else X.ticked.delete(t.dataset.axTick); refreshSum(); }
});

async function download(again: boolean): Promise<void> {
  const x = X!;
  const ids = [...x.ticked].map((k) => { const [type, id] = k.split(":"); return { type, id }; });
  const res = await fetch("/api/org/accounting-export/download", { method: "POST", credentials: "same-origin", headers: { "content-type": "application/json" }, body: JSON.stringify({ from: x.from, to: x.to, ids, again }) });
  if (!res.ok) {
    const e = (await res.json().catch(() => null))?.error;
    throw new ApiError(res.status, e?.code || "ERROR", e?.message || "Something went wrong. Please try again.", e?.field, e?.details);
  }
  const blob = await res.blob();
  const name = res.headers.get("x-file-name") || "falcon-export";
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
  toast(`Downloaded ${name}. The records are marked Exported.`);
}
let busy = false;
onAction("ax-download", async () => {
  if (!X || busy) return;
  const m = document.getElementById("ax-msg");
  if (!X.ticked.size) { if (m) { m.hidden = false; m.textContent = "Tick at least one record first."; } return; }
  busy = true;
  try {
    try { await download(false); }
    catch (err) {
      if (!(err instanceof ApiError) || err.code !== "ALREADY_EXPORTED") throw err;
      const on = formatDate(String(err.details?.exportedAt || "").slice(0, 10));
      if (!(await confirmBox(`These were already exported on ${on}. Export again?`, "Export again"))) return;
      await download(true);
    }
    await reload();
  } catch (err) {
    if (m) { m.hidden = false; m.textContent = (err as Error).message; }
  } finally { busy = false; }
});
